import React, { useState } from 'react';
import { login, requestPasswordReset } from './api';
import { useAuth } from './AuthContext';
import MerkLogo from './MerkLogo';

export default function LoginPage({ onMfaRequired, onMfaSetup, brand }) {
  // sessieMelding: waarom iemand hier (terug) staat — bv. "Je sessie is verlopen".
  const { loginWithToken, sessieMelding } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      // Verkopers en teamleaders zonder MFA-plicht krijgen het token meteen bij
      // het inloggen; alleen dan staat er een `token` in het antwoord. De server
      // beslist dat (rol + mfa_required) — hier niets over de rol afleiden.
      if (data.token) {
        loginWithToken(data.token, data.user);
      } else if (data.mfaSetupRequired) {
        onMfaSetup(data.userId, data.mfa.qrCode);
      } else {
        onMfaRequired(data.userId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const data = await requestPasswordReset(email);
      // De backend antwoordt altijd generiek (geen e-mail-enumeratie).
      setInfo(data.message || 'Als er een account met dit e-mailadres bestaat, is er een e-mail met instructies verstuurd.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const switchMode = (m) => { setMode(m); setError(''); setInfo(''); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <MerkLogo brand={brand} />
          <h1 className="text-2xl font-bold text-slate-100">{brand?.name || "Partner Portal"}</h1>
          <p className="text-slate-400 mt-1">{mode === 'login' ? 'Log in om verder te gaan' : 'Wachtwoord opnieuw instellen'}</p>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleSubmit} className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 space-y-5">
            {sessieMelding && !error && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-3 rounded-xl text-sm">{sessieMelding}</div>}
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-colors" placeholder="naam@bedrijf.nl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Wachtwoord</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-colors" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-lime-500 to-lime-600 text-slate-900 font-semibold rounded-xl hover:from-lime-400 hover:to-lime-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Bezig...' : 'Inloggen'}
            </button>
            <div className="text-center">
              <button type="button" onClick={() => switchMode('forgot')} className="text-sm text-slate-400 hover:text-lime-400 transition-colors">Wachtwoord vergeten?</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleForgot} className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 space-y-5">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>}
            {info ? (
              <div className="bg-lime-500/10 border border-lime-500/30 text-lime-300 px-4 py-3 rounded-xl text-sm">{info}</div>
            ) : (
              <>
                <p className="text-sm text-slate-400">Vul je e-mailadres in. Bestaat er een account, dan sturen we een link om een nieuw wachtwoord in te stellen (1 uur geldig). Je tweestapsverificatie (2FA) blijft ongewijzigd.</p>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-colors" placeholder="naam@bedrijf.nl" />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-lime-500 to-lime-600 text-slate-900 font-semibold rounded-xl hover:from-lime-400 hover:to-lime-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Bezig...' : 'Stuur reset-link'}
                </button>
              </>
            )}
            <div className="text-center">
              <button type="button" onClick={() => switchMode('login')} className="text-sm text-slate-400 hover:text-lime-400 transition-colors">← Terug naar inloggen</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
